import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';

import type { AgentCategory } from '@edgevanta/db/data-types';
import {
  AGENT_TYPES_VALUES,
  AGENT_CATEGORY_VALUES,
  AGENT_MODEL_VALUES,
  AGENT_REASONING_EFFORT_VALUES,
  AGENT_TOOL_CHOICE_VALUES,
  AGENT_FILE_SETTINGS_VALUES,
  schema,
} from '@edgevanta/db/drizzle/client';

export const agentSettingsSchema = z.object({
  model: z.enum(AGENT_MODEL_VALUES),
  temperature: z.number().min(0).max(2).default(0.0),
  presencePenalty: z.number().min(-2).max(2).default(0.0),
  frequencyPenalty: z.number().min(-2).max(2).default(0.0),
  maxRetries: z.number().int().min(0).max(5).default(2),
  maxSteps: z.number().int().min(1).default(10),
  maxOutputTokens: z.number().int().min(1).max(128000).optional().nullable(),
  reasoningEnabled: z.boolean().default(false),
  reasoningEffort: z.enum(AGENT_REASONING_EFFORT_VALUES).default('medium'),
  enabledTools: z.array(z.string()).default([]),
  toolChoice: z
    .union([z.enum(AGENT_TOOL_CHOICE_VALUES), z.string()])
    .default('auto'),
  stopOnToolCalled: z.string().optional().nullable(),
  fileSettings: z.enum(AGENT_FILE_SETTINGS_VALUES).default('none'),
  beta: z.boolean().default(false),
});

export const selectAgentSchema = createSelectSchema(schema.agents, {
  description: z.string().nullable(),
  userPrompt: z.string().nullable(),
  systemPrompt: z.string().nullable(),
  type: z.enum(AGENT_TYPES_VALUES),
  category: z.enum(AGENT_CATEGORY_VALUES),
  ...agentSettingsSchema.shape,
});

export const insertAgentSchema = createInsertSchema(schema.agents, {
  name: z.string().min(1),
  description: z.string().nullable(),
  userPrompt: z.string().nullable(),
  systemPrompt: z.string().nullable(),
  beta: z.boolean().default(false),
}).omit({
  type: true,
});

export const updateAgentSchema = insertAgentSchema.partial().extend({
  id: z.string(),
});

const AgentsQuerySchema = z.object({
  limit: z.coerce.number().optional().default(25),
  offset: z.coerce.number().optional(),
  page: z.coerce.number().optional(),
  name: z.string().optional(),
  type: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((type) => type);
    })
    .default([]),
  visibility: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((visibility) => visibility);
    })
    .default([]),
  category: z
    .string()
    .optional()
    .transform((val, ctx) => {
      if (!val) {
        return [] as AgentCategory[];
      }
      const allowed = Object.values(
        AGENT_CATEGORY_VALUES,
      ) as readonly AgentCategory[];
      const parts = val.split(',').map((c) => c.trim()).filter(Boolean);
      for (const part of parts) {
        if (!allowed.includes(part as AgentCategory)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid category: ${part}. Allowed: ${allowed.join(', ')}`,
          });
          return z.NEVER;
        }
      }
      return parts as AgentCategory[];
    })
    .default([]),
  createdAt: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((num) => Number(num));
    })
    .default([]),
  sort: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [
          {
            id: 'name',
            desc: false,
          },
        ];
      }

      try {
        const parsed = JSON.parse(val);
        const sortSchema = z.array(
          z.object({
            id: z.string(),
            desc: z.boolean(),
          }),
        );

        const result = sortSchema.safeParse(parsed);

        if (!result.success) {
          return [
            {
              id: 'name',
              desc: false,
            },
          ];
        }

        return result.data;
      } catch {
        return [
          {
            id: 'name',
            desc: false,
          },
        ];
      }
    })
    .default([
      {
        id: 'name',
        desc: false,
      },
    ]),
});

export const getAgentsQuerySchema = AgentsQuerySchema.merge(
  z.object({
    userId: z.string(),
  }),
);

export const getAgentsCountQuerySchema = AgentsQuerySchema;
